import { Component, For, JSX, Show } from "solid-js";
import type { GitHubIssue } from "../../api/workItems";

export interface GithubIssuePickerProps {
  issues: GitHubIssue[];
  selected: Set<number>;
  onSelectionChange: (next: Set<number>) => void;
  /** Left-hand content of the header row, opposite the select-all control. */
  label?: JSX.Element;
  /** Show each issue's open/closed badge. */
  showState?: boolean;
  /** Shown in place of the list when there are no issues. */
  emptyMessage?: string;
  headerClass?: string;
  listClass?: string;
}

export const GithubIssuePicker: Component<GithubIssuePickerProps> = (props) => {
  const allSelected = () =>
    props.issues.length > 0 && props.selected.size === props.issues.length;

  const toggleIssue = (issueNumber: number) => {
    const next = new Set(props.selected);
    if (next.has(issueNumber)) {
      next.delete(issueNumber);
    } else {
      next.add(issueNumber);
    }
    props.onSelectionChange(next);
  };

  const toggleAll = () => {
    props.onSelectionChange(
      props.selected.size === props.issues.length
        ? new Set<number>()
        : new Set(props.issues.map((i) => i.number)),
    );
  };

  return (
    <>
      <div class={props.headerClass ?? "flex items-center justify-between"}>
        {props.label}
        <button
          class="text-xs font-medium text-ink-2 hover:text-ink"
          onClick={toggleAll}
        >
          <Show when={allSelected()} fallback={<>Select all</>}>
            Deselect all
          </Show>
        </button>
      </div>

      <Show
        when={props.issues.length > 0}
        fallback={
          <Show when={props.emptyMessage}>
            <p class="text-sm text-ink-3 py-4">{props.emptyMessage}</p>
          </Show>
        }
      >
        <div
          class={`overflow-y-auto divide-y divide-line ${props.listClass ?? "max-h-80"}`}
        >
          <For each={props.issues}>
            {(issue) => (
              <label class="flex items-start gap-3 px-3 py-2.5 cursor-pointer hover:bg-sunken transition-colors">
                <input
                  type="checkbox"
                  checked={props.selected.has(issue.number)}
                  onChange={() => toggleIssue(issue.number)}
                  class="mt-0.5 accent-ink rounded"
                />
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <span class="text-xs text-ink-3 shrink-0">
                      #{issue.number}
                    </span>
                    <span class="text-sm text-ink-2 truncate">
                      {issue.title}
                    </span>
                  </div>
                  <Show when={issue.labels.length > 0}>
                    <div class="flex flex-wrap gap-1 mt-1">
                      <For each={issue.labels}>
                        {(label) => (
                          <span
                            class="text-xs px-1.5 py-0.5 rounded-full border"
                            style={{
                              "background-color": label.color
                                ? `#${label.color}20`
                                : undefined,
                              "border-color": label.color
                                ? `#${label.color}60`
                                : undefined,
                              color: label.color
                                ? `#${label.color}`
                                : undefined,
                            }}
                          >
                            {label.name}
                          </span>
                        )}
                      </For>
                    </div>
                  </Show>
                </div>
                <Show when={props.showState}>
                  <span
                    class={`text-xs shrink-0 px-1.5 py-0.5 rounded ${
                      issue.state === "open"
                        ? "bg-signal-green-tint text-signal-green"
                        : "bg-sunken text-ink-2"
                    }`}
                  >
                    {issue.state}
                  </span>
                </Show>
              </label>
            )}
          </For>
        </div>
      </Show>
    </>
  );
};
